import React from 'react';

import {
  BooleanField,
  BooleanInput,
  Create,
  Edit,
  Filter,
  Labeled,
  ReferenceField,
  ResourceContextProvider,
  required,
  SaveButton,
  SelectInput,
  Show,
  ShowButton,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  TextInput,
  Toolbar,
  useRecordContext,
} from 'react-admin';
import AutocompleteClientInput from './components/AutocompleteClientInput';
import FeatureGrantScopeField from './components/FeatureGrantScopeField';
import InfiniteList from './components/InfiniteList';
import { WriteOnlyDatagrid } from './components/WriteOnlyDatagrid';
import { WriteOnlyEditButton } from './components/WriteOnlyEditButton';

const KEY_HELPER =
  'Stable identifier referenced in code (e.g. "in_app_registration"). Cannot be changed after creation.';

const REQUIRES_FLAG_HELPER =
  'Optional split.io/Harness flag acting as a hard kill-switch: when set and off, the feature resolves disabled regardless of grants.';

const stateChoices = [
  { id: 'enabled', name: 'Enabled' },
  { id: 'disabled', name: 'Disabled' },
];

const scopeTypeChoices = [
  { id: 'client', name: 'Client' },
  { id: 'country', name: 'Country' },
  { id: 'studio', name: 'Studio' },
  { id: 'location', name: 'Location' },
  { id: 'user', name: 'User' },
];

const FeatureGrantsFilters: React.FC = (props) => (
  <Filter {...props}>
    <AutocompleteClientInput
      source="clientId"
      label="Client"
      size="small"
      alwaysOn
    />
    <SelectInput
      source="scopeType"
      label="Scope"
      choices={scopeTypeChoices}
      size="small"
      margin="none"
      alwaysOn
    />
  </Filter>
);

const FeatureToolbar = () => (
  <Toolbar>
    <SaveButton />
  </Toolbar>
);

const FeatureFilters: React.FC = (props) => (
  <Filter {...props}>
    <BooleanInput label="Archived" source="isArchived" alwaysOn />
  </Filter>
);

export const FeatureList: React.FC = () => (
  // empty={false} keeps the filter bar (and its Archived toggle) mounted even
  // when the filtered result is empty (e.g. every feature is archived).
  <InfiniteList filters={<FeatureFilters />} empty={false}>
    <WriteOnlyDatagrid bulkActionButtons={false}>
      <TextField source="key" sortable={false} />
      <TextField source="name" sortable={false} />
      <TextField source="defaultState" label="Default State" sortable={false} />
      <TextField
        source="requiresFeatureFlagKey"
        label="Requires Flag"
        sortable={false}
      />
      <BooleanField source="isArchived" label="Archived" sortable={false} />
      <WriteOnlyEditButton />
      <ShowButton />
    </WriteOnlyDatagrid>
  </InfiniteList>
);

// Grants for the feature being shown, scoped by featureId. Uses GET_LIST with
// the featureId filter (no GET_MANY_REFERENCE wiring needed).
const FeatureGrants: React.FC = () => {
  const record = useRecordContext();
  if (!record) {
    return null;
  }
  return (
    <ResourceContextProvider value="FeatureGrant">
      <InfiniteList
        filter={{ featureId: record.id }}
        filters={<FeatureGrantsFilters />}
        disableSyncWithLocation
        storeKey={`feature-${record.id}-grants`}
        actions={false}
        // Embedded in the feature's show page — don't override the title bar.
        title={false}
        empty={false}
      >
        <WriteOnlyDatagrid bulkActionButtons={false}>
          <ReferenceField
            source="client.id"
            reference="Client"
            label="Client"
            link="show"
            sortable={false}
          >
            <TextField source="name" />
          </ReferenceField>
          <TextField source="scopeType" label="Scope" sortable={false} />
          <FeatureGrantScopeField label="Scope Target" />
          <TextField source="state" sortable={false} />
        </WriteOnlyDatagrid>
      </InfiniteList>
    </ResourceContextProvider>
  );
};

export const FeatureShow: React.FC = () => (
  <Show>
    <SimpleShowLayout>
      <TextField source="id" label="ID" />
      <TextField source="key" label="Key" />
      <TextField source="name" label="Name" />
      <TextField source="description" label="Description" />
      <TextField source="defaultState" label="Default State" />
      <TextField source="requiresFeatureFlagKey" label="Requires Flag" />
      <BooleanField source="isArchived" label="Archived" />
      <TextField source="createdAt" label="Created" />
      <TextField source="updatedAt" label="Updated" />
      <Labeled label="Grants" fullWidth>
        <FeatureGrants />
      </Labeled>
    </SimpleShowLayout>
  </Show>
);

export const FeatureEdit: React.FC = () => (
  <Edit mutationMode="pessimistic" redirect={false}>
    <SimpleForm toolbar={<FeatureToolbar />}>
      <TextInput source="id" InputProps={{ disabled: true }} />
      <TextInput source="key" InputProps={{ disabled: true }} />
      <TextInput source="name" validate={required()} />
      <TextInput source="description" multiline />
      <SelectInput
        source="defaultState"
        label="Default State"
        choices={stateChoices}
        validate={required()}
      />
      <TextInput
        source="requiresFeatureFlagKey"
        label="Requires Flag"
        helperText={REQUIRES_FLAG_HELPER}
      />
      <BooleanInput source="isArchived" label="Archived" />
    </SimpleForm>
  </Edit>
);

export const FeatureCreate: React.FC = () => (
  <Create redirect="list">
    <SimpleForm>
      <TextInput source="key" validate={required()} helperText={KEY_HELPER} />
      <TextInput source="name" validate={required()} />
      <TextInput source="description" multiline />
      <SelectInput
        source="defaultState"
        label="Default State"
        choices={stateChoices}
        defaultValue="disabled"
        validate={required()}
      />
      <TextInput
        source="requiresFeatureFlagKey"
        label="Requires Flag"
        helperText={REQUIRES_FLAG_HELPER}
      />
    </SimpleForm>
  </Create>
);
